import { loadedDictionaries } from '@/i18n/locales'
import { getPreferences } from '@/stores/preferences.store'
import { type JSX, createEffect, onCleanup, onMount, splitProps } from 'solid-js'
import { type LatentLoaderElement, registerLatentLoader } from './latent-runtime.js'
import './Loader.css'

export type LoaderShape = 'cascade' | 'radial'

export interface LoaderProps extends JSX.HTMLAttributes<HTMLSpanElement> {
  size?: number
  tone?: 'brand' | 'current'
  shape?: LoaderShape
  state?: 'loading' | 'success'
  speed?: number
  paused?: boolean
  label?: string
}

export function Loader(props: LoaderProps) {
  const [local, rest] = splitProps(props, [
    'size',
    'tone',
    'shape',
    'state',
    'speed',
    'paused',
    'label',
    'class',
    'style',
    'children',
  ])
  let host!: HTMLSpanElement
  const size = () => Math.min(512, Math.max(12, local.size ?? 24))

  onMount(() => {
    registerLatentLoader()
    const globe = document.createElement('peatch-loader') as LatentLoaderElement
    globe.setAttribute('aria-hidden', 'true')
    host.append(globe)
    createEffect(() => {
      globe.setAttribute('size', String(Math.max(16, size())))
      globe.setAttribute('tone', local.tone === 'current' ? 'current' : 'brand')
      globe.setAttribute('shape', local.shape ?? 'cascade')
      globe.setAttribute('state', local.state ?? 'loading')
      globe.setAttribute('speed', String(local.speed ?? 1))
      globe.toggleAttribute('paused', Boolean(local.paused))
    })
    onCleanup(() => globe.remove())
  })

  return (
    <span
      {...rest}
      ref={host}
      class={`ds-loader ${local.tone === 'current' ? 'ds-loader--current' : ''} ${local.class ?? ''}`}
      style={
        typeof local.style === 'string'
          ? `--loader-size:${size()}px;${local.style}`
          : { '--loader-size': `${size()}px`, ...local.style }
      }
      role={local.label ? 'status' : undefined}
      aria-label={local.label}
      aria-hidden={local.label ? undefined : true}
      data-state={local.state ?? 'loading'}
    />
  )
}

export interface LoadingStateProps extends JSX.HTMLAttributes<HTMLDivElement> {
  size?: number
  label?: string
  compact?: boolean
}

export function LoadingState(props: LoadingStateProps) {
  const [local, rest] = splitProps(props, ['size', 'label', 'compact', 'class', 'children'])
  const label = () =>
    local.label ??
    loadedDictionaries()[getPreferences().language]?.['common.loading'] ??
    loadedDictionaries().en?.['common.loading']
  return (
    <div
      {...rest}
      class={`ds-loading-state ${local.compact ? 'ds-loading-state--compact' : ''} ${local.class ?? ''}`}
      role="status"
      aria-busy="true"
      aria-label={label()}
    >
      <Loader size={local.size ?? (local.compact ? 24 : 64)} />
      {local.label && <span class="ds-loading-state__label">{local.label}</span>}
      {local.children}
    </div>
  )
}
